import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Textarea} from '@/components/ui/textarea'
import {ControlEvidenceBadge} from '@/components/ipevr/control-evidence-panel'
import {
  useAddFilaControl,
  useControlCatalog,
  useControlHistory,
  useRemoveFilaControl,
  type Fila,
  type RiesgoFilaControlTipo,
} from '@/hooks/sgsst/riesgos/use-ipevr'
import {marcarErrorComoComunicado} from '@/lib/errors'
import {normalize} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

/** Tres capas de opciones: catálogo según la clasificación, historial de la empresa y texto libre.
 * Toda entrada se quita igual, también la detectada en el cuestionario: decide el dueño. */
export function ControlDimensionEditor({
  businessId,
  fila,
  tipo,
  canEdit,
}: {
  businessId: string
  fila: Fila
  tipo: RiesgoFilaControlTipo
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const [query, setQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const {data: catalog} = useControlCatalog(businessId, fila.clasificacion, tipo)
  const {data: history} = useControlHistory(businessId, tipo)
  const addControl = useAddFilaControl(businessId)
  const removeControl = useRemoveFilaControl(businessId)
  const [error, setError] = useState<string | null>(null)

  const entries = fila.controles.filter((c) => c.tipo === tipo)
  const selectedKeys = useMemo(() => new Set(entries.map((c) => normalize(c.texto))), [entries])
  const normalizedQuery = normalize(query)

  const catalogMatches = useMemo(() => {
    if (!catalog) return []
    return catalog
      .filter((c) => !selectedKeys.has(normalize(c.texto)) && normalize(c.texto).includes(normalizedQuery))
      .slice(0, 6)
  }, [catalog, normalizedQuery, selectedKeys])

  const historyMatches = useMemo(() => {
    if (!history) return []
    const catalogTextos = new Set((catalog ?? []).map((c) => normalize(c.texto)))
    return history
      .filter(
        (texto) =>
          !selectedKeys.has(normalize(texto)) &&
          !catalogTextos.has(normalize(texto)) &&
          normalize(texto).includes(normalizedQuery)
      )
      .slice(0, 6)
  }, [history, catalog, normalizedQuery, selectedKeys])

  const exactExists = useMemo(() => {
    const inCatalog = (catalog ?? []).some((c) => normalize(c.texto) === normalizedQuery)
    const inHistory = (history ?? []).some((texto) => normalize(texto) === normalizedQuery)
    return inCatalog || inHistory
  }, [catalog, history, normalizedQuery])
  const canCreate = !!normalizedQuery && !selectedKeys.has(normalizedQuery) && !exactExists

  function add(texto: string, catalogKey?: string) {
    const trimmed = texto.trim()
    if (!trimmed) return
    setError(null)
    addControl.mutate(
      {filaId: fila.id, control: {tipo, texto: trimmed, catalog_key: catalogKey ?? null}},
      {
        // Avisa a la red global que el fallo ya se explicó: si no, pinta su burbuja y lo reporta a Sentry.
        onError: (err) => {
          marcarErrorComoComunicado(err)
          setError(t('ipevr.controles.addError'))
        },
      }
    )
    setQuery('')
  }

  function remove(controlId: string) {
    setError(null)
    removeControl.mutate(
      {filaId: fila.id, controlId},
      {
        onError: (err) => {
          marcarErrorComoComunicado(err)
          setError(t('ipevr.controles.removeError'))
        },
      }
    )
  }

  return (
    <div className="space-y-2" data-testid={`control-editor-${tipo}`}>
      <span className="flex items-center gap-1.5">
        <p className="text-sm font-medium text-foreground">{t(`ipevr.controles.tipo.${tipo}`)}</p>
      </span>

      {entries.length > 0 && (
        <div className="flex flex-col gap-2">
          {entries.map((c) => (
            <div key={c.id} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span
                data-testid={`control-chip-${tipo}`}
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground">
                <span className="min-w-0 truncate">{c.texto}</span>
                {c.origen === 'cuestionario' && (
                  <span
                    data-testid="control-chip-origen-cuestionario"
                    className="shrink-0 font-mono text-[9px] tracking-wider uppercase opacity-70">
                    {t('ipevr.controles.origenCuestionario')}
                  </span>
                )}
                {canEdit && (
                  <button
                    type="button"
                    aria-label={t('ipevr.controles.remove', {texto: c.texto})}
                    data-testid={`control-chip-remove-${tipo}`}
                    onClick={() => remove(c.id)}
                    disabled={removeControl.isPending}
                    className="shrink-0 font-normal opacity-60 hover:opacity-100">
                    ×
                  </button>
                )}
              </span>
              <ControlEvidenceBadge
                businessId={businessId}
                controlId={c.id}
                evidenciaHint={c.evidencia_hint}
                fotos={c.fotos}
                canEdit={canEdit}
              />
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div className="relative">
          <Textarea
            maxLength={tope('BUSCA')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={t('ipevr.controles.placeholder')}
            minRows={1}
            maxRows={6}
            data-testid={`control-input-${tipo}`}
          />
          {focused && (catalogMatches.length > 0 || historyMatches.length > 0 || canCreate) && (
            <ul
              className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-border bg-popover shadow-[var(--shadow-pop)]"
              // El onBlur del textarea llega antes del onClick de la opción: sin esto el clic se pierde.
              onMouseDown={(e) => e.preventDefault()}>
              {catalogMatches.length > 0 && (
                <li className="px-3 py-1 font-mono text-[9px] font-medium tracking-wider text-muted-foreground uppercase">
                  {t('ipevr.controles.catalogGroup')}
                </li>
              )}
              {catalogMatches.map((c) => (
                <li key={c.key}>
                  <button
                    type="button"
                    data-testid={`control-option-catalog-${tipo}`}
                    onClick={() => add(c.texto, c.key)}
                    className="flex w-full items-start px-3 py-2 text-left text-sm hover:bg-secondary">
                    <span className="min-w-0 break-words">{c.texto}</span>
                  </button>
                </li>
              ))}
              {historyMatches.length > 0 && (
                <li className="border-t border-border px-3 py-1 font-mono text-[9px] font-medium tracking-wider text-muted-foreground uppercase">
                  {t('ipevr.controles.historyGroup')}
                </li>
              )}
              {historyMatches.map((texto) => (
                <li key={texto}>
                  <button
                    type="button"
                    data-testid={`control-option-history-${tipo}`}
                    onClick={() => add(texto)}
                    className="flex w-full items-start px-3 py-2 text-left text-sm hover:bg-secondary">
                    <span className="min-w-0 break-words">{texto}</span>
                  </button>
                </li>
              ))}
              {canCreate && (
                <li
                  className={
                    catalogMatches.length > 0 || historyMatches.length > 0 ? 'border-t border-border' : undefined
                  }>
                  <button
                    type="button"
                    data-testid={`control-option-create-${tipo}`}
                    onClick={() => add(query)}
                    className="flex w-full items-start px-3 py-2 text-left text-sm text-primary hover:bg-secondary">
                    <span className="min-w-0 break-words">{t('ipevr.controles.create', {texto: query.trim()})}</span>
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
      {error && (
        <p className="text-xs text-destructive" data-testid={`control-error-${tipo}`}>
          {error}
        </p>
      )}
    </div>
  )
}
